import { ForwardedRef, forwardRef, useId } from "react";

import {
  Box,
  CircularProgress,
  FormHelperText,
  Autocomplete as MUIAutocomplete,
  MenuItem,
} from "@mui/material";
import { rem } from "utils";

import { useAutocomplete } from "./Autocomplete.hooks";
import {
  Container,
  Input,
  SelectedIconWrapper,
  StyledPopper,
} from "./Autocomplete.styles";
import {
  AutocompleteProps,
  AutocompleteValue,
  AutocompleteReference,
} from "./Autocomplete.types";
import { CheckIcon, ChevronDownIcon, XIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";

const AutocompleteInner = <V, R>(
  {
    color,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputRef,
    label,
    loading,
    options = [],
    placeholder,
    prefix,
    readOnly,
    required,
    suffix,
    value,
    tooltipText,
    disabledLocalSearch = false,
    renderOption,
    onChange,
    onInputChange,
    onFocus,
    onLoadMore,
  }: AutocompleteProps<AutocompleteValue<V>>,
  ref: ForwardedRef<R>
) => {
  const { autocompleteRef, valueState, onChangeHandler } = useAutocomplete({
    ref,
    defaultValue,
    value,
    onChange,
  });
  const formId = useId();

  return (
    <Container>
      {label && (
        <InputLabel
          htmlFor={id ?? formId}
          required={required}
          helperText={tooltipText}
        >
          {label}
        </InputLabel>
      )}
      <MUIAutocomplete
        fullWidth
        disablePortal={false}
        ref={autocompleteRef}
        id={id ?? formId}
        color={color}
        disabled={disabled}
        readOnly={readOnly}
        loading={loading}
        options={options}
        value={valueState}
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            const bottomThreshold = 10;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - bottomThreshold
            ) {
              if (onLoadMore) onLoadMore();
            }
          },
        }}
        popupIcon={<ChevronDownIcon fontSize="small" />}
        clearIcon={<XIcon fontSize="small" />}
        isOptionEqualToValue={(option, v) => option.value === v.value}
        onChange={(_event, selectedValue) => onChangeHandler(selectedValue)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFocus={onFocus as any}
        getOptionDisabled={(option) => option.value === "DISABLED"}
        onInputChange={(_e, v, r) => onInputChange?.(v, r)}
        renderOption={(props, option, state) => (
          <MenuItem {...props}>
            {typeof renderOption === "function"
              ? renderOption(props, option, state)
              : option.label}
            {state.selected ? (
              <SelectedIconWrapper>
                <CheckIcon color="primary" fontSize="small" />
              </SelectedIconWrapper>
            ) : null}
          </MenuItem>
        )}
        renderInput={(params) => (
          <Input
            {...params}
            color={color}
            inputRef={inputRef}
            required={required}
            disabled={disabled}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {prefix ? (
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      marginX={rem(6)}
                    >
                      {prefix}
                    </Box>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={rem(14)} />
                  ) : null}
                  {suffix}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        PopperComponent={StyledPopper}
        {...(disabledLocalSearch && { filterOptions: (opts) => opts })}
      />
      {helperText && (
        <FormHelperText disabled={disabled} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Container>
  );
};

const Autocomplete = forwardRef(AutocompleteInner) as <V, D>(
  props: AutocompleteProps<AutocompleteValue<V, D>> & {
    ref?: ForwardedRef<AutocompleteReference<AutocompleteValue<V, D>>>;
  }
) => ReturnType<typeof AutocompleteInner>;

export default Autocomplete;
